<template>
    <div class="container-fluid">
        <h1>{{ title }}</h1>
        <br />
        <!-- Form -->
        <b-form
            ref="releasenoteform"
            name="releasenoteForm"
            v-on:submit.prevent="$_onSubmit"
            class="pb-5"
        >
            <div class="col-xs-8 col-md-6">
                <b-container>
                    <b-row>
                        <b-col v-if="userInfo.isSuper">
                            <!-- Version -->
                            <TextInput
                                :text="labels.version"
                                v-model="record.version"
                                :id="'releasenote-version'"
                                :name="'releasenote-version'"
                                :invalid="nameMessage"
                                :valid="nameMessage"
                                :plaintext="!userInfo.isSuper"
                                :state="!errorInUse"
                                @input="$_exists"
                                :required="userInfo.isSuper"
                                autofocus
                            >
                            </TextInput>
                        </b-col>
                        <b-col>
                            <label v-show="errorInUse" class="errormsg">{{
                                $t('versionInUse')
                            }}</label>
                            <!-- LastChangedBy -->
                            <TextInput
                                v-if="this.userInfo.isSuper"
                                :text="labels.changed_by"
                                v-model="record.lastChangedBy"
                                :id="'releasenote-editby'"
                                :name="'releasenote-editby'"
                                :invalid="nameMessage"
                                :valid="nameMessage"
                                plaintext
                            >
                            </TextInput>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <!-- ReleaseDate -->
                            <div>
                                <base-input
                                    v-if="this.userInfo.isSuper"
                                    id="datepicker"
                                    :text="labels.date"
                                    :extendedClass="{
                                        'form-control-plaintext':
                                            !userInfo.isSuper
                                    }"
                                    :type="inputType"
                                    v-model="record.releaseDate"
                                    :required="userInfo.isSuper"
                                >
                                </base-input>
                                <TextInput
                                    v-if="!this.userInfo.isSuper"
                                    :text="labels.date"
                                    v-model="record.releaseDate"
                                    :id="datepicker"
                                    plaintext
                                >
                                </TextInput>
                            </div>
                        </b-col>
                        <b-col>
                            <!-- LastChangedOn-->
                            <TextInput
                                v-if="this.userInfo.isSuper"
                                :text="labels.edit_date"
                                v-model="record.lastChangedDate"
                                :id="'releasenote-changeddate'"
                                :name="'releasenote-changeddate'"
                                :invalid="nameMessage"
                                :valid="nameMessage"
                                plaintext
                            >
                            </TextInput>
                        </b-col>
                    </b-row>
                    <!-- Notes, can be highlighted and filtered based on searchTerm -->
                    <b-row v-show="!changeReleasenote">
                        <b-col>
                            <!-- Search field -->
                            <search-box
                                @input="$_onInputSearch"
                                @keydown.enter.prevent
                                :info="$t('searchInfo')"
                                class="override-max-width"
                                :value="localSearchtext"
                            >
                            </search-box>
                        </b-col>
                        <b-col>
                            <!-- Release note bijwerken, options for superuser only to switch to input mode -->
                            <div
                                v-show="
                                    userInfo.isSuper && isEditMode
                                "
                            >
                                <base-button
                                    @click="changeReleasenote = true"
                                    :text="labels.update"
                                    :iconClass="`fa fa-pencil-alt`"
                                >
                                </base-button>
                            </div>
                        </b-col>
                    </b-row>
                </b-container>
            </div>
            <div class="col-xs-8 col-md-6">
                <div v-show="!changeReleasenote">
                    <br />
                    <!-- Show notes, highlighting the search text -->
                    <div v-html="$_highlight()"></div>
                </div>
                <!-- Input notes for superuser (when switched to input mode) -->
                <div v-show="changeReleasenote">
                    <x-textarea
                        :id="'releaseNotes'"
                        :name="'releaseNotes'"
                        :text="labels.notes"
                        :rows="20"
                        :cols="140"
                        class="mb-5"
                        v-model="record.notes"
                        :hasResizeHandle="true"
                        :showCounter="true"
                        :plaintext="!userInfo.isSuper"
                        :maxlength="8000"
                        :required="userInfo.isSuper"
                    >
                    </x-textarea>
                </div>
                <div v-show="userInfo.isSuper">
                    <div class="sticky-bottom fixed-bottom">
                        <div class="btn-group" role="group">
                            <b-button
                                type="submit"
                                id="btnSaveReleaseNotes"
                                name="btnSaveReleaseNotes"
                                variant="primary"
                                :disabled="inProgress"
                            >
                                {{ $t('btnSaveLabel') }}
                                <span
                                    :class="{
                                        'fa fa-check': !isSaving,
                                        'fas fa-sync fa-spin': isSaving
                                    }"
                                ></span>
                            </b-button>
                            <b-button
                                id="btnSendReleaseNotes"
                                name="btnSendReleaseNotes"
                                v-if="newReleaseNotesAdded"
                                @click="$_sendEmail"
                                class="btn-space-5"
                                :disabled="releaseNotesSent || inProgress"
                            >
                                {{ emailButtonText }}
                                <span
                                    v-bind:class="{
                                        'fa fa-envelope': !isSending,
                                        'fas fa-sync fa-spin': isSending
                                    }"
                                ></span>
                            </b-button>
                            <b-button
                                id="btnCancelReleaseNotes"
                                name="btnCancelReleaseNotes"
                                @click="$_onCancel"
                                class="btn-space-5"
                                :disabled="inProgress"
                            >
                                <span class="fas fa-times"></span>
                                {{ $t('btnCancelLabel') }}
                            </b-button>
                        </div>
                        <div
                            class="btn-group btn-space-5"
                            role="group"
                            v-show="isEditMode"
                        >
                            <b-button
                                id="btnDeleteReleaseNotes"
                                name="btnDeleteReleaseNotes"
                                @click="$_onRemove"
                                :disabled="inProgress"
                            >
                                {{ $t('btnDeleteLabel') }}
                                <span
                                    :class="{
                                        'fa fa-trash-alt': !isDeleting,
                                        'fas fa-sync fa-spin': isDeleting
                                    }"
                                ></span>
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </b-form>
    </div>
</template>

<script>
    import { MODE_ADD, MODE_EDIT } from '../../constants';
    import { nprogress } from '../../services/utilities';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import TextInput from '../../components/BaseComponents/x-input.vue';
    import XTextarea from '../../components/BaseComponents/x-textarea.vue';
    import SearchBox from '../../components/SearchBox.vue';
    import BaseButton from '../../components/BaseComponents/base-button.vue';
    import BaseInput from '../../components/BaseComponents/base-input.vue';

    export default {
        mixins: [modalMixin],

        components: {
            TextInput,
            'search-box': SearchBox,
            'x-textarea': XTextarea,
            'base-button': BaseButton,
            'base-input': BaseInput
        },

        props: {
            id: { type: Number, required: false, default: -1 },
            searchtext: { type: String, required: false, default: '' }
        },

        data: function () {
            return {
                userInfo: {},
                record: {},

                mode: 0,
                title: '',

                changeReleasenote: false,

                nameMessage: '',
                formattedNotes: '',

                isSaving: false,
                isSending: false,
                isDeleting: false,

                labels: {
                    version: this.$t('columnNameVersion'),
                    date: this.$t('columnNameReleaseDate'),
                    changed_by: this.$t('columnNameChangedBy'),
                    edit_date: this.$t('columnNameChangedDate'),
                    update: this.$t('labelReleaseNoteUpdate'),
                    notes: this.$t('menuItemReleaseNotes')
                },
                errorInUse: false,
                localSearchtext: '',
                newReleaseNotesAdded: false,
                releaseNotesSent: false
            };
        },

        created() {
            this.userInfo = this.$store.getters.userInfo;
        },

        computed: {
            inputType() {
                return this.userInfo.isSuper ? 'date' : 'text';
            },
            inProgress() {
                return this.isSaving || this.isDeleting || this.isSending;
            },
            emailButtonText() {
                return this.releaseNotesSent ? this.$t('releaseNotesEmailVerstuurd') : this.$t('releaseNotesEmailVersturen');
            },
            isEditMode() {
                return this.mode === MODE_EDIT;
            }
        },

        async mounted() {
            if (this.id && this.id > 0) {
                this.mode = MODE_EDIT;
                if (this.userInfo.isSuper) {
                    this.title = this.$t('releaseNotesEditTitle');
                } else {
                    this.title = this.$t('releaseNotesTitle');
                }

                nprogress.start();

                this.localSearchtext = this.searchtext.slice(0);
                // Get existing data from the backend
                await this.$_GetExistingReleaseNotes();

                nprogress.done();
            } else {
                this.mode = MODE_ADD;
                this.title = this.$t('releaseNotesDetailAddTitle');
                this.changeReleasenote = true;
            }
        },

        methods: {
            /**
             * Checks if the given version exists in the database
             * @return {void} - Sets error message if needed
             */
            async $_exists() {
                await this.$store.dispatch('useApiData', {
                    name: 'GetReleaseNoteExists',
                    parameters: { version: this.record.version }
                })
                    .then((result) => {
                        this.errorInUse = result.data;
                    });
            },

            /**
             *  Filted the releaseNotes based on searchtext
             */
            $_filterNotes: function () {
                // In case of empty searchtext, formatted notes are equal to original notes
                if (this.localSearchtext === '') {
                    this.formattedNotes = this.record.notes;
                } else {
                    // If there is a searchtext, we filter the array
                    // Split the release notes on newline
                    let lineArray = this.record.notes.split('\n');

                    let upperText = this.localSearchtext.toUpperCase();

                    // Filter out the lines that do not include the searchtext
                    let filteredLineArray = _.filter(lineArray, function (line) {
                        return line.toUpperCase().includes(upperText);
                    });

                    // Recreate string for display
                    let tempString = '';

                    filteredLineArray.forEach(function (filteredLine) {
                        tempString += filteredLine;
                        tempString += '\n\n';
                    });

                    // Get rid of final 2 newlines
                    tempString = tempString.substring(0, tempString.length - 2);

                    // Assign to scope variable which is used for display
                    this.formattedNotes = tempString;
                }
            },
            async $_GetExistingReleaseNotes() {
                // Get existing data from the backend
                await this.$store.dispatch('useApiData', {
                    name: 'GetReleaseNoteDetail',
                    parameters: { id: this.id }
                })
                    .then((result) => {
                        this.$_setReleaseNoteRecord(result);

                        // Format the notes to a paragraph for normal user, necessary for highlighting the search term
                        this.formattedNotes = this.record.notes;

                        // Filter the notes, only show lines that contain the searchTerm
                        this.$_filterNotes();
                    });
            },

            /**
             * Filter for highlighting the searchtext in de releasenotes
             * @param {string} text newsItem text
             * @return {string}     a paragraph with the highlighted text
             */
            $_highlight: function () {
                let text = this.formattedNotes
                    .replace(/</gi, '&lt;')
                    .replace(/>/gi, '&gt;');
                const phrase = this.localSearchtext;

                // Create paragraph tags
                let releaseNoteOpenParagraph = '<p style="white-space: pre-line;">';
                let releaseNoteCloseParagraph = '</p>';

                // Escape special characters for regex
                // See: https://stackoverflow.com/questions/3115150/how-to-escape-regular-expression-special-characters-using-javascript
                let escapedRegexPhrase = phrase.replace(
                    /[-[\]{}()*+?.,\\^$|#\s]/g,
                    '\\$&'
                );

                // Replace phrase with a highlighted span
                if (escapedRegexPhrase && text) {
                    text = text.replace(
                        new RegExp(`(${escapedRegexPhrase})`, 'gi'),
                        '<span class="highlighted">$1</span>'
                    );
                }

                // Recreate the paragraph HTML and return
                return releaseNoteOpenParagraph + text + releaseNoteCloseParagraph;
            },

            $_onInputSearch(newValue) {
                this.localSearchtext = newValue;
                this.$_filterNotes();
            },

            $_gotoOverview() {
                this.$router.push({ path: '/dashboard/releasenotes' });
            },

            $_onSubmit() {
                if (
                    this.userInfo.isSuper &&
                    this.$refs.releasenoteform.checkValidity()
                ) {
                    this.$_onSave(false);
                }
            },

            async $_onSave() {
                this.isSaving = true;
                const releasenote = Object.assign({}, this.record);
                nprogress.start();

                // Save the release note
                let name = (releasenote.id === undefined ||
                            releasenote.id === null ||
                            releasenote.id === 0) ?
                    'CreateReleaseNote' : 'UpdateReleaseNote';
                await this.$store.dispatch('useApiData', {
                    name: name,
                    data: releasenote
                })
                .then((result) => {
                    if (result && result.data) {
                        const data = {
                            message: this.$t(
                                'releaseNoteCardSavedConfirmation'
                            ),
                            variant: 'success',
                            autoHideDelay: 2500,
                            noCloseButton: true
                        };

                        if (result.data.id !== 0) {
                            if (this.mode !== MODE_EDIT) {
                                this.mode = MODE_EDIT;
                                // add option to email new release notes
                                this.newReleaseNotesAdded = true;
                            }
                            this.$_setReleaseNoteRecord(result);
                            this.showToast(data);
                            this.isSaving = false;
                        }
                    }
                })
                .catch((error) => {
                    nprogress.done();
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t('saveFailureTitle'),
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                    this.isSaving = false;
                });
                nprogress.done();
            },

            $_onCancel() {
                this.$_gotoOverview();
            },

            async $_onRemove() {
                nprogress.start();
                const messageData = {
                    message: this.$t('releaseNoteCardDeletionsConfirmation')
                };
                this.msgBoxConfirm(messageData).then(async (confirm) => {
                    if (confirm) {
                        this.isDeleting = true;
                        await this.$store.dispatch('useApiData', {
                            name: 'DeleteReleaseNote',
                            parameters: { id: this.record.id }
                        })
                            .then(() => {
                                this.$_gotoOverview();
                                this.isDeleting = false;
                            })
                            .catch((error) => {
                                const data = {
                                    message: this.$t('deleteFailureMessage', [
                                        `${error.response.data.message}`
                                    ]),
                                    variant: 'danger',
                                    title: this.$t('deleteFailureTitle'),
                                    autoHideDelay: 10000
                                };
                                this.showToast(data);
                                this.isDeleting = false;
                            });
                    }
                });
                nprogress.done();
            },
            /**
             * Set and format releasenoteDetail result
             * @param {Object} result releasenoteDetail from db
             */
            $_setReleaseNoteRecord(result) {
                this.record = Object.assign({}, result.data);

                // Take date only
                this.record.releaseDate = this.record.releaseDate.split('T')[0];
                this.record.lastChangedDate =
                    this.record.lastChangedDate.split('T')[0];
            },
            async $_sendEmail() {
                nprogress.start();
                this.isSending = true;
                // Email the release notes
                await this.$store.dispatch('useApiData', {
                    name: 'EmailReleaseNote',
                    parameters: { id: this.record.id }
                }).then((result) => {
                    let data = null;
                    if (result && result.data) {
                        data = {
                            message: this.$t(
                                'releaseNotesSendConfirmation'
                            ),
                            variant: 'success',
                            autoHideDelay: 2500,
                            noCloseButton: true
                        };
                        this.releaseNotesSent = true;
                    } else {
                        data = {
                            message: this.$t('releaseNotesSendFailure'),
                            variant: 'danger',
                            autoHideDelay: 10000
                        };
                    }
                    this.showToast(data);
                })
                .catch((error) => {
                    nprogress.done();
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t('releaseNotesSendFailureMessage'),
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                });
                this.isSending = false;
                nprogress.done();
            }
        }
    };
</script>

<style scoped>
.container {
    padding-left: 0px;
    margin-left: initial;
}

.search-group {
    max-width: initial;
    padding-left: 0px;
    padding-right: 0px;
}

.override-max-width {
    max-width: 100%;
    padding-right: 0px;
    padding-left: 0px;
}

.email {
    width: 130px;
    height: 30px;
}
</style>
